import axios from "axios";
import base64 from "base64-js";
import { isMobileDevice } from "../../utils/deviceDetection.util";
import {
  SET_IS_SCREEN_SHARED,
  SET_SCREEN_STREAM,
  SET_IS_SCREEN_SHARING_REQUIRED,
} from "./actionTypes";
import {
  BASE_URL,
  handleDynamicRequestHeader,
} from "../../Common/CommonExport";
import { toast } from "react-toastify";

export const setIsScreenSharingRequired = (isScreenSharingRequired) => ({
  type: SET_IS_SCREEN_SHARING_REQUIRED,
  payload: isScreenSharingRequired,
});

export const setIsScreenShared = (isScreenShared) => ({
  type: SET_IS_SCREEN_SHARED,
  payload: isScreenShared,
});

export const askScreenShare = () => async (dispatch) => {
  if (isMobileDevice()) {
    console.warn("Screen sharing skipped: Mobile device detected.");
    return true; // Return true so it doesn't block progress
  }
  try {
    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: {
        displaySurface: "monitor",
      },
      monitorTypeSurfaces: "include", // Legacy Chrome
    });

    // strict check for entire screen
    const track = stream.getVideoTracks()[0];
    const settings = track.getSettings();
    if (settings.displaySurface && settings.displaySurface !== "monitor") {
      stream.getTracks().forEach((t) => t.stop());
      toast.error("Please share your entire screen.");
      return;
    }

    dispatch(setScreenStream(stream));
    const videoTracks = stream.getVideoTracks();
    const ScreenOn =
      videoTracks.length > 0 &&
      videoTracks.every((track) => track.readyState === "live");

    dispatch(setIsScreenShared(ScreenOn));
    dispatch(setIsScreenSharingRequired(true));

    // Add track ended listener to detect when user stops sharing via browser UI
    videoTracks[0].onended = () => {
      dispatch(setIsScreenShared(false));
      dispatch(setScreenStream(null));
    };

    stream.oninactive = () => {
      dispatch(setIsScreenShared(false));
      dispatch(setScreenStream(null));
    };
    return ScreenOn;
  } catch (error) {
    console.error("Screen share error:", error);
    dispatch(setIsScreenShared(false));
    return false;
  }
};

export const stopScreenShare = (screenStream) => async (dispatch) => {
  if (screenStream && typeof screenStream.getTracks === "function") {
    screenStream.getTracks().forEach((track) => track?.stop());
  }
  dispatch(setScreenStream(null));
  dispatch(setIsScreenShared(false));
};

export const takeScreenSnapshot =
  (stream, testId, inviteId) => async (dispatch) => {
    // Validate stream before attempting to use it
    if (!stream || typeof stream.getVideoTracks !== "function") {
      console.warn("Invalid stream object provided to takeScreenSnapshot");
      return;
    }

    const videoTrack = stream.getVideoTracks()[0];
    if (!videoTrack) {
      console.warn("No video track available in stream");
      return;
    }

    try {
      // Try ImageCapture first if supported, but wrap in its own try-catch for fallback
      if (window.ImageCapture) {
        try {
          const imageCapture = new ImageCapture(videoTrack);
          const bitmap = await imageCapture.grabFrame();
          const canvas = document.createElement("canvas");
          canvas.width = bitmap.width;
          canvas.height = bitmap.height;
          const context = canvas.getContext("2d");
          context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
          const snapshotData = canvas.toDataURL("image/jpeg");
          return await dispatch(uploadSnapshot(snapshotData, testId, inviteId));
        } catch (imageCaptureErr) {
          console.warn(
            "ImageCapture failed or is not supported for screen track, falling back to video element",
            imageCaptureErr,
          );
        }
      }

      // Fallback: Use a hidden video element to capture the frame
      return await new Promise((resolve, reject) => {
        const video = document.createElement("video");
        video.autoplay = true;
        video.playsInline = true;
        video.muted = true;

        // Create a new stream from the track to avoid mutating the original
        video.srcObject = new MediaStream([videoTrack]);

        video.onloadeddata = async () => {
          try {
            const canvas = document.createElement("canvas");
            canvas.width = video.videoWidth || 1280;
            canvas.height = video.videoHeight || 720;
            const context = canvas.getContext("2d");
            context.drawImage(video, 0, 0, canvas.width, canvas.height);

            video.srcObject = null; // Clean up

            const snapshotData = canvas.toDataURL("image/jpeg");
            const result = await dispatch(
              uploadSnapshot(snapshotData, testId, inviteId),
            );
            resolve(result);
          } catch (err) {
            video.srcObject = null;
            reject(err);
          }
        };

        video.onerror = (err) => {
          console.warn("Hidden video capture error", err);
          video.srcObject = null;
          resolve(null); // Resolve to null instead of rejecting so we don't block
        };
      });
    } catch (error) {
      console.error("Error taking screen snapshot:", error);
    }
  };

export const uploadSnapshot =
  (snapshot, testId, inviteId) => async (dispatch, getState) => {
    return new Promise(async (resolve, reject) => {
      const base64Data = snapshot.replace(
        /^data:image\/(png|jpeg|jpg);base64,/,
        "",
      );
      const imageBytes = base64.toByteArray(base64Data);

      try {
        const state = getState();
        const questionId =
          state.currentQuestion?.data?._id ||
          state.currentQuestion?.data?.questionId;

        const fileData = {
          requirement: [
            {
              order: 0,
              extension: "jpg",
            },
          ],
          // module: "candidateScreenSnapShots",
          module: `clientId/job_title_jobId/assessments/name_assessmentId/submission`,
          moduleType: "assessment_submission",
          clientId: state.testReducer.clientId,
          jobId: state.testReducer.jobId,
          assessmentId: testId,
          questionId: questionId,
          candidateId: inviteId,
          candidateName: state.candidateReducer.name,
          clientName: state.testReducer.company,
          assessmentName: state.testReducer.name,
        };

        const method = "POST";
        const fileUploadUrl = `${BASE_URL}/public/file`;
        const wrongFileUploadResponse = (error) => {
          if (!axios.isCancel(error) && !error.cancelled) {
            toast.error("Error in uploading file data");
          }
          reject(error);
        };

        const rightFileUploadResponse = async (fileResponse) => {
          const fileId = fileResponse.data[0].id || fileResponse.data[0]._id;
          const uploadURL = fileResponse.data[0].url;

          // Perform actual upload
          if (uploadURL) {
            try {
              await axios.put(uploadURL, imageBytes, {
                headers: {
                  "Content-Type": "image/jpeg",
                },
              });
            } catch (e) {
              console.error("Cloud upload failed", e);
            }
          }

          resolve(fileId);

          const method = "POST";
          let url = `${BASE_URL}/public/assessment/${testId}/upload-candidate-screen-snapshots?candidateAssessmentId=${inviteId}`;
          if (questionId) {
            url += `&questionId=${questionId}`;
          }

          const wrongResponse = () => {
            // toast.error("Error in uploading screen snapshot image");
          };

          const rightResponse = async (data) => {
            // Success
          };

          await handleDynamicRequestHeader(
            method,
            url,
            { screenSnapShotIds: fileId, questionId: questionId },
            null,
            wrongResponse,
            rightResponse,
          );
        };

        await handleDynamicRequestHeader(
          method,
          fileUploadUrl,
          fileData,
          null,
          wrongFileUploadResponse,
          rightFileUploadResponse,
        );
      } catch (error) {
        console.log(error);
        reject(error);
      }
    });
  };

export const setScreenStream = (screenStream) => ({
  type: SET_SCREEN_STREAM,
  payload: screenStream,
});
